import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { format, formatDistanceStrict, addDays, isBefore } from 'date-fns';
import { IoIosArrowBack, IoMdTrash } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  Input,
  InputDate,
  Layout,
  Modal,
  Select,
  Textarea,
} from '~/components';
import { usePackages } from '~/hooks/packages';
import { useScreen } from '~/hooks/screen';
import api from '~/services/api';
import {
  currencyFormatter,
  currencyToNumberFormatter,
} from '~/utils/formatter';
import getValidationErrors from '~/utils/getValidationErrors';
import { regex_remove_all_non_digits } from '~/utils/regex';

import { PackageOption } from '../BillingRegister';
import { BillingProps } from './interface';

import {
  Container,
  DeleteButton,
  DeleteModalContainer,
  FieldSet,
  FormLabel,
  PageHeader,
  PageHeaderActionBtnsContainer,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  PageHeaderSaveButton,
  RadioInput,
  SelectContainer,
} from './styles';

interface RouteParams {
  userId: string;
  billingId: string;
}

type Package = {
  id: number;
  professional_id: number;
  gym_id: number;
  name: string;
  lealtad: string;
  status: string;
  value: number;
  number_of_training_programs: number;
  created_at: string;
  updated_at: string;
};

type SelectOption = {
  value: number;
  label: string;
};

type FormProps = {
  package_id?: string;
  start_date: string;
  end_date: string;
  expiration_day: string;
  payment_form: string;
  monthly_amount: string;
  total_amount: string;
  status: SelectOption;
  observations: string;
};

export default function EditBilling() {
  const { goBack, location } = useHistory();
  const { userId: studentId, billingId } = useParams<RouteParams>();
  const { loadPackages, packages } = usePackages();
  const { updateScreen } = useScreen();

  const [billing] = useState<BillingProps>(location.state as BillingProps);

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [defaultFormData, setDefaultFormData] = useState<FormProps>(
    {} as FormProps,
  );
  const [selectedPackageId, setSelectedPackageId] = useState<number>(0);
  const [startDate, setStartDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [selectedPayWay, setSelectedPayWay] = useState('billet');
  // TODO ver aqui
  const [apiPackages, setApiPackages] = useState<Package[]>([]);
  const [packagesListOptions, setPackagesListOptions] = useState<
    PackageOption[]
  >([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fidelizationWay, setFidelizationWay] = useState('');
  const fidelizations = useMemo(
    () => [
      { id: 'monthly', label: 'Mensal', value: 1 },
      { id: 'quarterly', label: 'Trimestral', value: 3 },
      { id: 'semiannual', label: 'Semestral', value: 6 },
      { id: 'yearly', label: 'Anual', value: 12 },
    ],
    [],
  );

  useEffect(() => {
    loadPackages();
    updateScreen(`billing`);
  }, [loadPackages, updateScreen]);

  // ? Pega os dados da fatura e preencher o formulário
  useEffect(() => {
    const {
      package_id,
      start_date,
      end_date,
      expiration_day,
      payment_form,
      monthly_amount,
      total_amount,
      status,
      observations,
    } = billing;

    const formData = {
      package_id,
      start_date: format(
        addDays(new Date(start_date ?? Date.now()), 1),
        'yyyy-MM-dd',
      ),
      end_date: format(
        addDays(new Date(end_date ?? Date.now()), 1),
        'yyyy-MM-dd',
      ),
      expiration_day,
      payment_form,
      monthly_amount: currencyFormatter(monthly_amount ?? 0),
      total_amount: currencyFormatter(total_amount ?? 0),
      status,
      observations,
      number_of_training_programs:
        billing.package?.number_of_training_programs || 0,
      lealtad: billing.package?.lealtad,
    };

    formRef.current?.setData(formData);
    setDefaultFormData(formData as unknown as FormProps);
    setFidelizationWay(billing?.package?.lealtad as string);

    setSelectedPackageId(package_id ?? 0);
    setSelectedPayWay(payment_form ?? 'billet');
    setStartDate(start_date ?? format(new Date(Date.now()), 'yyyy-MM-dd'));
    setEndDate(end_date ?? format(new Date(Date.now()), 'yyyy-MM-dd'));
  }, [billing]);

  // ? Pega o valor mensal do pacote selecionado
  const setMonthlyAmount = useCallback(
    (id: number | string) => {
      const selectedPackage = packages.filter(
        (_package: Package) => id === _package.id,
      )[0];

      formRef.current?.setFieldValue(
        'monthly_amount',
        currencyFormatter(selectedPackage?.value),
      );
    },
    [packages],
  );

  // ? Calculo para dar o valor total
  useEffect(() => {
    const selectedPackage = packages.filter(
      (_package: Package) => selectedPackageId === _package.id,
    )[0];

    try {
      const mothDistance = formatDistanceStrict(
        new Date(startDate),
        new Date(endDate),
        { unit: 'month' },
      ).replace(regex_remove_all_non_digits, '');

      selectedPackage &&
        formRef.current?.setFieldValue(
          'total_amount',
          currencyFormatter(selectedPackage?.value * (+mothDistance || 1)),
        );
    } catch (err) {
      //
    }
  }, [selectedPackageId, packages, startDate, endDate]);

  // ? Lista do select de status da fatura
  const financialStatusOptions = useMemo(
    () => [
      { value: 'pending', label: 'Pendente' },
      { value: 'paid', label: 'Paga' },
      { value: 'overdue', label: 'Vencida' },
      { value: 'canceled', label: 'Cancelada' },
    ],
    [],
  );

  // ? Listagem dos pacotes disponíveis
  const packagesMemo = useMemo(
    () =>
      packages.map((_package: Package) => ({
        value: Number(_package.id),
        label: _package.name,
      })),
    [packages],
  );

  // ? Radio select de formas de pagamento
  const payWayVariations = useMemo(
    () => [
      { value: 'billet', label: 'Boleto' },
      { value: 'cash', label: 'Dinheiro' },
      { value: 'credit_card', label: 'Cartão de crédito' },
      { value: 'debit_card', label: 'Cartão de débito' },
      { value: 'transfer', label: 'Transferência' },
      { value: 'others', label: 'Outros' },
    ],
    [],
  );

  const RadioStatusOptions = useMemo(
    () =>
      payWayVariations.map((item) => (
        <RadioInput
          key={item.label}
          isSelected={item.value === selectedPayWay}
          onClick={() => setSelectedPayWay(item.value)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [payWayVariations, selectedPayWay],
  );

  // ? submit  do formulário
  const handleSubmit = useCallback(
    async (data: FormProps) => {
      try {
        const schema = Yup.object().shape({
          student_id: Yup.number().required(),
          start_date: Yup.string().required('O campo é obrigatório!'),
          end_date: Yup.string().required('O campo é obrigatório!'),
          expiration_day: Yup.string().required('O campo é obrigatório!'),
          payment_form: Yup.string().required(),
          monthly_amount: Yup.number().required('O campo é obrigatório!'),
          total_amount: Yup.number().required('O campo é obrigatório!'),
          status: Yup.string().required('O campo é obrigatório!'),
        });

        const formData = {
          // ...data,
          student_id: +studentId,
          start_date: data.start_date,
          end_date: data.end_date,
          expiration_day: data.expiration_day,
          payment_form: selectedPayWay,
          monthly_amount: currencyToNumberFormatter(data.monthly_amount),
          total_amount: currencyToNumberFormatter(data.total_amount),
          status: data.status,
          observations: data.observations,
        };

        const IsInitialDateBeforeEndDate = isBefore(
          new Date(formData.end_date),
          new Date(formData.start_date),
        );

        if (IsInitialDateBeforeEndDate) {
          setLoading(false);

          toast.error(
            'Erro ao editar fatura!\nA data de termino não pode ser anterior a data de início!',
          );

          return;
        }

        await schema.validate(formData, { abortEarly: false });

        await api.put(`billings/${billingId}`, formData);

        toast.success('Fatura editada com sucesso!');

        goBack();
      } catch (err) {
        console.log(err);

        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        toast.error('Erro ao editar fatura!');
      }
    },
    [studentId, billingId, selectedPayWay, goBack],
  );

  const inputStyle = {
    input: {
      height: 35,
      padding: '0 1.7rem',
    },
    select: {
      placeholder: () => ({
        padding: '0 1rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    },
    textArea: {
      width: '100%',
      marginTop: 5,

      height: '19rem',
      padding: '2rem',
      borderRadius: '1rem',
    },
  };

  const FidelizationOptions = useMemo(
    () =>
      fidelizations.map((item) => (
        <RadioInput key={item.label} isSelected={item.id === fidelizationWay}>
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [fidelizations, fidelizationWay],
  );

  const handleDeleteBilling = useCallback(
    async (id: string | number) => {
      await api.delete(`billings/${id}`);
      goBack();
    },
    [goBack],
  );

  function DeleteModal() {
    return (
      <Modal
        isOpen={showDeleteModal}
        setOpen={setShowDeleteModal}
        maxHeight="280px"
      >
        <DeleteModalContainer>
          {/* <h1>Atenção</h1> */}

          <p>
            VOCÊ DESEJA DELETAR A FATURA DE NÚMERO <strong>{billing.id}</strong>
            ?
            <br />
            ESSA AÇÃO APAGARÁ TODO O HISTÓRICO DE PROGRAMAS DE TREINO
          </p>

          <div>
            <button
              type="button"
              onClick={() => handleDeleteBilling(billing.id as number)}
            >
              Sim
            </button>

            <button type="button" onClick={() => setShowDeleteModal(false)}>
              Não
            </button>
          </div>
        </DeleteModalContainer>
      </Modal>
    );
  }

  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get('packages');

      const filteredPackages = data.data?.filter(
        (_package: Package) => _package.status === 'active',
      );

      setApiPackages(filteredPackages);

      const mappedPackages = filteredPackages.map((_package: Package) => ({
        value: Number(_package.id),
        label: _package.name,
      }));

      setPackagesListOptions(mappedPackages);
    }

    fetchData();

    setLoading(false);
  }, []);

  return (
    <Layout title="Aluno">
      <DeleteModal />

      <PageHeader>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack />
          <PageHeaderBackButtonLabel>editar fatura</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <PageHeaderActionBtnsContainer>
          <PageHeaderSaveButton
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            Salvar
          </PageHeaderSaveButton>

          <DeleteButton type="button" onClick={() => setShowDeleteModal(true)}>
            <IoMdTrash color="#fff" size={30} />
          </DeleteButton>
        </PageHeaderActionBtnsContainer>
      </PageHeader>

      <Container>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={defaultFormData}
        >
          <div>
            <FieldSet>
              <FormLabel>Pacote:</FormLabel>
              <SelectContainer>
                <Select
                  isDisabled
                  name="package_id"
                  isSearchable={false}
                  placeholder="Selecionar"
                  defaultValue={billing.package}
                  options={packagesMemo}
                  onChange={(e) => {
                    setMonthlyAmount(e?.value);
                    setSelectedPackageId(e?.value);
                  }}
                  styles={inputStyle.select}
                />
              </SelectContainer>
            </FieldSet>

            <FieldSet>
              <FormLabel>Fidelização:</FormLabel>
              {FidelizationOptions}
            </FieldSet>

            <FieldSet>
              <FormLabel>Programas de treino:</FormLabel>
              <Input
                disabled
                name="number_of_training_programs"
                placeholder="Programas de treino"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <FormLabel>Valor mensal:</FormLabel>
              <Input
                disabled
                name="monthly_amount"
                placeholder="R$0,00"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <FormLabel>Valor total:</FormLabel>
              <Input
                disabled
                name="total_amount"
                placeholder="R$0,00"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <FormLabel>Data de início:</FormLabel>
              <InputDate
                disabled
                name="start_date"
                onChange={(e) => {
                  setStartDate(e.target.value);
                }}
                placeholder="Data de início"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <FormLabel>Data de término:</FormLabel>
              <InputDate
                disabled
                name="end_date"
                placeholder="Data de término"
                onChange={(e) => {
                  setEndDate(e.target.value);
                }}
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>
          </div>

          <div>
            <FieldSet>
              <FormLabel>Dia de vencimento:</FormLabel>
              <Input
                name="expiration_day"
                defaultValue={1}
                placeholder="Dia do vencimento"
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </FieldSet>

            <FieldSet>
              <FormLabel>Meio de pagamento:</FormLabel>
              {RadioStatusOptions}
            </FieldSet>

            <FieldSet>
              <FormLabel>Status financeiro:</FormLabel>

              <SelectContainer>
                <Select
                  name="status"
                  isSearchable={false}
                  placeholder="Selecionar"
                  defaultValue={billing.status}
                  options={financialStatusOptions}
                  styles={inputStyle.select}
                />
              </SelectContainer>
            </FieldSet>

            <FieldSet>
              <FormLabel>Dados adicionais:</FormLabel>
              <Textarea
                name="observations"
                containerStyle={inputStyle.textArea}
                fontSize="1.5rem"
              />
            </FieldSet>
          </div>
        </Form>
      </Container>
    </Layout>
  );
}
